/**
 * Auth Modal State Management Hook
 * 
 * Centralized state management for login modal visibility and navigation.
 * Replaces page-based authentication redirects with modal-based flow.
 */

import { useState, useCallback, createContext, useContext, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface AuthModalContextValue {
  isModalOpen: boolean;
  pendingNavigation: string | null;
  openLoginModal: (redirectTo?: string) => void;
  closeLoginModal: () => void;
  requireAuth: (redirectTo: string) => boolean;
}

const AuthModalContext = createContext<AuthModalContextValue | undefined>(undefined);

interface AuthModalProviderProps {
  children: ReactNode;
}

export function AuthModalProvider({ children }: AuthModalProviderProps) {
  const { isAuthenticated } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  /**
   * Open login modal with optional redirect destination
   */
  const openLoginModal = useCallback((redirectTo?: string) => {
    setPendingNavigation(redirectTo || null);
    setIsModalOpen(true);
  }, []);

  /**
   * Close login modal and clear pending navigation
   */
  const closeLoginModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingNavigation(null);
  }, []);

  /**
   * Check if user is authenticated, show modal if not
   * Returns true if authenticated, false if modal was shown
   */
  const requireAuth = useCallback((redirectTo: string): boolean => {
    if (isAuthenticated) {
      return true;
    }
    
    openLoginModal(redirectTo);
    return false;
  }, [isAuthenticated, openLoginModal]);

  const value: AuthModalContextValue = {
    isModalOpen,
    pendingNavigation,
    openLoginModal,
    closeLoginModal,
    requireAuth,
  };

  return (
    <AuthModalContext.Provider value={value}>
      {children}
    </AuthModalContext.Provider>
  );
}

/**
 * Hook to use auth modal context
 */
// eslint-disable-next-line react-refresh/only-export-components -- #3601 hook co-located with its provider
export function useAuthModal(): AuthModalContextValue {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
}

/**
 * Convenience hook for components that need authentication
 * Returns a function that shows login modal if not authenticated
 */
// eslint-disable-next-line react-refresh/only-export-components -- #3601 hook co-located with its provider
export function useRequireAuth() {
  const { requireAuth } = useAuthModal();
  return requireAuth;
}